import { Check } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ProgressRing } from "@/components/ui/progress-ring";

interface HorizontalPhaseIndicatorProps {
  currentSubphase: string;
  clarityScore: number;
  answeredInSubphase: number;
  totalInSubphase: number;
  onSubphaseClick?: (subphaseId: string) => void;
}

const SUBPHASES = [
  { id: "1.1", label: "Entscheidungsfokus", shortLabel: "Fokus", stepLabel: "Erfolg messbar machen" },
  { id: "1.2", label: "Spannungsfeld", shortLabel: "Spannung", stepLabel: "Zielkonflikte aufdecken" },
  { id: "1.3", label: "Kernannahme", shortLabel: "Annahme", stepLabel: "Hypothese prüfen" },
  { id: "1.4", label: "Synthese", shortLabel: "Synthese", stepLabel: "Entscheidungsfrage formen" },
];

export const HorizontalPhaseIndicator = ({
  currentSubphase,
  clarityScore,
  answeredInSubphase,
  totalInSubphase,
  onSubphaseClick,
}: HorizontalPhaseIndicatorProps) => {
  const currentIndex = SUBPHASES.findIndex(sp => sp.id === currentSubphase);

  const handleSubphaseClick = (subphaseId: string, index: number) => {
    // Only allow clicking on completed subphases
    if (index < currentIndex && onSubphaseClick) {
      onSubphaseClick(subphaseId);
    }
  };

  return (
    <div className="mb-6 animate-fade-in">
      <div className="max-w-3xl mx-auto px-4">
        {/* Current Step Label - prominent */}
        <div className="text-center mb-4">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-primary">
            Schritt {currentIndex + 1} von {SUBPHASES.length}
          </span>
          <p className="text-sm font-medium text-foreground mt-0.5">
            {SUBPHASES[currentIndex]?.stepLabel || ""}
          </p>
        </div>

        <TooltipProvider delayDuration={200}>
          <div className="relative">
            {/* Progress Line Background */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-border/30" 
                 style={{ left: '2rem', right: '2rem' }} />
            
            {/* Active Progress Line */}
            <div 
              className="absolute top-5 left-0 h-0.5 bg-primary transition-all duration-500"
              style={{ 
                left: '2rem',
                width: `calc(${(currentIndex / (SUBPHASES.length - 1)) * 100}% - 2rem)`,
              }}
            />

            {/* Steps */}
            <div className="relative flex items-center justify-between">
              {SUBPHASES.map((subphase, index) => {
                const isCompleted = index < currentIndex;
                const isCurrent = index === currentIndex;
                const isUpcoming = index > currentIndex;

                return (
                  <Tooltip key={subphase.id}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleSubphaseClick(subphase.id, index)}
                        disabled={!isCompleted}
                        className={cn(
                          "flex flex-col items-center gap-2 group",
                          isCompleted && "cursor-pointer hover:opacity-80",
                          !isCompleted && "cursor-default"
                        )}
                      >
                        {/* Step Circle - larger and bolder for current */}
                        <div
                          className={cn(
                            "rounded-full border-2 flex items-center justify-center transition-all duration-300 relative",
                            isCompleted && "w-10 h-10 bg-primary border-primary shadow-md",
                            isCurrent && "w-12 h-12 bg-background border-primary border-[3px] shadow-xl ring-4 ring-primary/25",
                            isUpcoming && "w-10 h-10 bg-background border-border/40"
                          )}
                        >
                          {isCompleted ? (
                            <Check className="w-5 h-5 text-primary-foreground" />
                          ) : isCurrent ? (
                            <div className="w-4 h-4 rounded-full bg-primary animate-pulse" />
                          ) : (
                            <div className="w-2 h-2 rounded-full bg-muted-foreground/20" />
                          )}
                        </div>

                        {/* Step Label - bolder for current */}
                        <div className="text-center">
                          <div
                            className={cn(
                              "text-xs transition-colors",
                              isCurrent && "text-foreground font-bold",
                              isCompleted && "text-foreground font-medium",
                              isUpcoming && "text-muted-foreground/60 font-normal"
                            )}
                          >
                            <span className="hidden sm:inline">{subphase.label}</span>
                            <span className="sm:hidden">{subphase.shortLabel}</span>
                          </div>
                          <div className={cn(
                            "text-[10px] mt-0.5",
                            isCurrent ? "text-primary font-semibold" : "text-muted-foreground/50"
                          )}>
                            {subphase.id}
                          </div>
                        </div>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[200px]">
                      <div className="space-y-1">
                        <p className="font-semibold">{subphase.label}</p>
                        {isCurrent && (
                          <p className="text-xs text-muted-foreground">
                            {answeredInSubphase} von {totalInSubphase} Fragen beantwortet
                          </p>
                        )}
                        {isCompleted && (
                          <>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Check className="w-3 h-3" /> Abgeschlossen
                            </p>
                            <p className="text-xs text-primary font-medium">
                              Klicke, um zurückzukehren
                            </p>
                          </>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>

            {/* Clarity Score Badge */}
            {clarityScore > 0 && (
              <div className="flex justify-center mt-4">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-card border border-border/50 rounded-full shadow-sm">
                  <ProgressRing 
                    progress={clarityScore} 
                    size={24} 
                    strokeWidth={3}
                    className="text-primary"
                  />
                  <span className="text-xs font-medium text-foreground">
                    {clarityScore}% Klärungsfortschritt
                  </span>
                </div>
              </div>
            )}
          </div>
        </TooltipProvider>
      </div>
    </div>
  );
};
